import type { ThemeColorName } from '@examdojo/styles';

/**
 * Variant options for label styling.
 * - 'primary': white text on feature color 500 background
 * - 'secondary': feature color 600 text on feature color 100 background (default)
 * - 'tertiary': feature color 700 text, no background
 */
export const LABEL_VARIANTS = ['primary', 'secondary', 'tertiary'] as const;
export type LabelVariant = (typeof LABEL_VARIANTS)[number];

/**
 * Text alignment options for multi-line labels.
 */
export const LABEL_TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type LabelTextAlign = (typeof LABEL_TEXT_ALIGNS)[number];

/**
 * Result of label style computation.
 */
export interface LabelStyleResult {
  backgroundColor: string;
  textColor: string;
}

/**
 * Label position relative to the element.
 *
 * For **points**: Positions are relative to the point location.
 *
 * For **lines/segments**: The label is placed at the midpoint.
 * - `left`/`right`: Perpendicular to the line direction, relative to the
 *   outer set points (start → end). `left` is on the left side when looking
 *   from start to end, `right` is on the right side.
 * - `top`/`bottom`: Above or below the midpoint (in screen coordinates).
 *
 * Use the `offset` property in `ElementLabelConfig` for fine-tuning placement.
 */
export const LABEL_POSITIONS = [
  'top',
  'bottom',
  'left',
  'right',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'center',
  'canvasTop',
  'canvasBottom',
  'canvasLeft',
  'canvasRight',
  'canvasTopLeft',
  'canvasTopRight',
  'canvasBottomLeft',
  'canvasBottomRight',
  'canvasCenter',
] as const;
export type LabelPosition = (typeof LABEL_POSITIONS)[number];

/**
 * Content variant for label text generation.
 * Provides convenient shortcuts for common label formats.
 */
export const LABEL_CONTENT_VARIANTS = [
  'name',
  'coord',
  'name+coord',
  'deg',
  'name+deg',
  'length',
  'name+length',
  'expression',
] as const;
export type LabelContentVariant = (typeof LABEL_CONTENT_VARIANTS)[number];

/**
 * Context for point labels.
 */
export interface PointLabelContext {
  type: 'point';
  name: string;
  x: () => number;
  y: () => number;
  expression?: () => string;
}

/**
 * Context for line labels.
 */
export interface LineLabelContext {
  type: 'line';
  name: string;
  length: () => number;
  expression?: () => string;
}

/**
 * Context for polygon labels.
 */
export interface PolygonLabelContext {
  type: 'polygon';
  name: string;
  area: () => number;
  perimeter: () => number;
  expression?: () => string;
}

/**
 * Context for curve labels.
 */
export interface CurveLabelContext {
  type: 'curve';
  name: string;
  /** Reactive LaTeX expression string for the function, e.g. "$2(x-3)^2+1$" */
  expression?: () => string;
}

/**
 * Context for angle labels.
 */
export interface AngleLabelContext {
  type: 'angle';
  name: string;
  degrees: () => number;
  radians: () => number;
  expression?: () => string;
}

/**
 * Discriminated union of all label contexts.
 */
export type LabelContext =
  | PointLabelContext
  | LineLabelContext
  | PolygonLabelContext
  | CurveLabelContext
  | AngleLabelContext;

/**
 * Extended label configuration for elements.
 */
export interface ElementLabelConfig {
  show?: boolean;
  variant?: LabelVariant;
  color?: ThemeColorName;
  /** Position of the label relative to the element */
  position?: LabelPosition;
  /** Content variant for automatic text generation */
  contentVariant?: LabelContentVariant;
  /** Custom format function for complex programmatic logic (overrides contentVariant) */
  formatFn?: (context: LabelContext) => string;
  /**
   * Label text with optional template placeholders for dynamic values.
   * Overrides both contentVariant and formatFn.
   *
   * If text contains `{placeholder}` syntax, values are interpolated dynamically.
   * Otherwise, text is displayed as-is (static).
   *
   * Available placeholders depend on element type:
   * - Points: `{name}`, `{x}`, `{y}`
   * - Lines: `{name}`, `{length}`
   * - Angles: `{name}`, `{deg}`, `{rad}`
   * - Polygons: `{name}`, `{area}`, `{perimeter}`
   *
   * @example "Hello" → "Hello" (static text)
   * @example "Δx = {length}" → "Δx = 5.00" (template)
   * @example "{name} at ({x}, {y})" → "A at (2.00, 3.00)" (template)
   */
  text?: string;
  /**
   * Additional pixel offset from the computed position [dx, dy].
   * This is added to the position-based offset for fine-tuning label placement.
   * Positive dx moves right, positive dy moves up.
   * @example [10, -5] // Move 10px right and 5px down from computed position
   */
  offset?: [number, number];
  /**
   * Number of decimal places for numeric values in this label.
   * Overrides the default precision for coordinates, lengths, degrees, etc.
   * @example 0 → "2", 1 → "2.0", 3 → "2.000"
   */
  precision?: number;
  /**
   * When true, the label is hidden by default and shown on hover over the element.
   */
  popup?: boolean;
  /**
   * Font size in pixels. Overrides the theme default.
   */
  fontSize?: number;
  /**
   * Text alignment for multi-line labels.
   */
  textAlign?: LabelTextAlign;
  /**
   * Where along a line/curve to place the label anchor. Ignored for points/angles/circles.
   * - `'start'` → 0 (beginning of line/curve domain)
   * - `'middle'` → 0.5 (default)
   * - `'end'` → 1 (end of line/curve domain)
   * - number → clamped to [0, 1]
   * @default 0.5
   */
  anchorAt?: number | 'start' | 'middle' | 'end';
}

/**
 * Resolved label configuration with all required fields.
 */
export interface ResolvedLabelConfig {
  show: boolean;
  variant: LabelVariant;
  color: ThemeColorName;
  position: LabelPosition;
  /** True when the user explicitly set `position` (not using the default). */
  positionIsExplicit: boolean;
  contentVariant: LabelContentVariant;
  formatFn?: (context: LabelContext) => string;
  text?: string;
  offset: [number, number];
  precision?: number;
  popup?: boolean;
  fontSize?: number;
  textAlign?: LabelTextAlign;
  anchorAt?: number | 'start' | 'middle' | 'end';
}

/**
 * Normalize a label config input to a resolved config.
 */
export function resolveLabelConfig(
  config: ElementLabelConfig | boolean | undefined,
  defaults: Partial<ResolvedLabelConfig> = {},
): ResolvedLabelConfig | null {
  if (config === false || config === undefined) {
    return null;
  }

  if (config === true) {
    return {
      show: true,
      variant: defaults.variant ?? 'secondary',
      color: defaults.color ?? 'neutral',
      position: defaults.position ?? 'topRight',
      positionIsExplicit: false,
      contentVariant: defaults.contentVariant ?? 'name',
      offset: defaults.offset ?? [0, 0],
    };
  }

  if (config.show === false) {
    return null;
  }

  return {
    show: config.show ?? true,
    variant: config.variant ?? defaults.variant ?? 'secondary',
    color: config.color ?? defaults.color ?? 'neutral',
    position: config.position ?? defaults.position ?? 'topRight',
    positionIsExplicit: config.position !== undefined,
    contentVariant: config.contentVariant ?? defaults.contentVariant ?? 'name',
    formatFn: config.formatFn,
    text: config.text,
    offset: config.offset ?? defaults.offset ?? [0, 0],
    precision: config.precision ?? defaults.precision,
    popup: config.popup ?? defaults.popup,
    fontSize: config.fontSize ?? defaults.fontSize,
    textAlign: config.textAlign ?? defaults.textAlign,
    anchorAt: config.anchorAt ?? defaults.anchorAt,
  };
}
